import axios from 'axios';
import _ from 'lodash';
import { TIMEOUT } from '../../comm/config';
import { store } from '../../redux';
import { LOADING_HIDE, LOADING_SHOW } from '../../redux/constant';
import { alertMsg } from '../commlogic';
import { getAuthToken } from '../../utils';
import { i18n } from '../../language';
import { showLoading, hideLoading } from '../loadingGuard';
const AxiosRequestConfig = {
  timeout: TIMEOUT,
};

const instance = axios.create(AxiosRequestConfig);

instance.defaults.headers.post['Content-Type'] = 'text/plain; charset=utf-8;';

const REFRESH_TOKEN_API_END_POINT = `/user/refreshtoken`;

const errorControlClass = () => {
  let count = 0;
  const hide = () => {
    if (count > 0) {
      count--;
    }
  };
  const show = () => {
    count++;
  };
  const isShow = () => {
    return count > 0;
  };
  return {
    hide,
    show,
    isShow,
  };
};
let errorControl = errorControlClass();

instance.interceptors.request.use((config) => {
  const shouldSkipLoading = Boolean(config.headers?.isHideLoading);
  config._internalSkipLoading = shouldSkipLoading;

  if (config.headers?.isHideLoading) {
    delete config.headers.isHideLoading;
  }

  if (!config.headers?.isHideError) {
    errorControl.show();
  }

  const getAuthorization = () => {
    const Authorization = getAuthToken();
    if (Authorization) {
      const url = config.url;
      if (url.includes(`/wpauthenticate/${REFRESH_TOKEN_API_END_POINT}`)) {
        return {};
      }
      return { Authorization };
    } else {
      return {};
    }
  };

  config.headers = {
    ...config.headers,
    ...getAuthorization(),
  };

  if (!shouldSkipLoading) {
    const reqId = showLoading(config.url);
    config._requestId = reqId;
  }

  return config;
});

instance.interceptors.response.use(
  (response) => {
    const { url, _requestId: reqId, _internalSkipLoading: shouldSkip } = response.config;

    // Use imported guard
    if (!shouldSkip && reqId) {
      hideLoading(url, reqId, false);
    } else if (!shouldSkip && url) {
      // Fallback
      hideLoading(url, null, false);
    }

    errorControl.hide();
    return response.data;
  },
  (error) => {
    const { url, _requestId: reqId, _internalSkipLoading: shouldSkip } = error.config || {};

    // Use imported guard
    if (!shouldSkip && reqId) {
      hideLoading(url, reqId, false);
    } else if (!shouldSkip && url) {
      // Fallback
      hideLoading(url, null, false);
    }
    const { response } = error;
    const isShow = errorControl.isShow();
    errorControl.hide();
    if (isShow) {
      const isRefreshToken = response.request.responseURL.includes(`${REFRESH_TOKEN_API_END_POINT}`);
      if (response.status === 401) {
        if (isRefreshToken) {
          setTimeout(() => {
            alertMsg(
              i18n.t('ALERT_IDLE_DETECTED_YOU_ARE_SIGNED_OUT_BECAUSE_OF_SECURITY_CONCERN'),
              _.uniqueId('warning_'),
              'warning'
            );
          }, 300);
        } else {
          setTimeout(() => {
            alertMsg(i18n.t('ALERT_CORP_ID_PASSWORD_IS_NOT_CORRECT_PLEASE_CHECK_AND_TRY_AGAIN'), _.uniqueId('error_'));
          }, 300);
        }
      } else {
        setTimeout(() => {
          alertMsg(i18n.t('ALERT_NETWORK_ERROR'), _.uniqueId('error_'));
        }, 300);
      }
    }
    return Promise.reject(error);
  }
);

/**
 * Simple Http Client
 * @param {string} method
 * @param {string} url
 * @param {object} param
 * @param {object} AxiosConfig see https://github.com/axios/axios#request-config
 */
const httpClient = (method, url, params, AxiosConfig) => {
  let httpConfig = {};
  httpConfig = {
    method,
    url,
    params: method === 'get' || method === 'delete' ? params : null,
    data: method === 'post' ? params : null,
    ...AxiosConfig,
  };
  return new Promise((resolve, reject) => {
    instance(httpConfig)
      .then((res) => {
        resolve(res);
      })
      .catch((error) => {
        reject(error);
      });
  });
};

export const AxiosInstance = instance;

export default {
  /**
   * @param {string} url
   * @param {object} params optional
   * @param {object} AxiosConfig optional see https://github.com/axios/axios#request-config
   */
  post(url, params = null, AxiosConfig = null) {
    return httpClient('post', url, params, AxiosConfig);
  },

  /**
   * @param {string} url
   * @param {object} params optional
   * @param {object} AxiosConfig optional https://github.com/axios/axios#request-config
   */
  get(url, params = null, AxiosConfig = null) {
    return httpClient('get', url, params, AxiosConfig);
  },

  delete(url, params = null, AxiosConfig = null) {
    return httpClient('delete', url, params, AxiosConfig);
  },
};
